// AUTO-GENERATED from shared/legal/documents.ts — do not edit. Run npm run sync:shared.
/**
 * SOURCE OF TRUTH — `shared/legal/documents.ts`. Do not edit the generated
 * copies in `src/types/legal/shared/`, `web/src/legal/shared/` or
 * `marketing_web/src/legal/shared/`; edit this file and run
 * `npm run sync:shared`.
 *
 * FNDTN-33: where the hosted Terms of Service and Privacy Policy live.
 *
 * Each document is one Firestore doc, `legal_documents/{id}`, holding the
 * text that is live now. Every version ever published is also kept as an
 * immutable snapshot at `legal_documents/{id}/versions/{version}`, so the
 * version a student accepted always maps back to the exact text they saw.
 * `firestore.rules` holds the write side to the same shape: a main-doc write
 * passes only if its body matches the snapshot of the version it names.
 *
 * The app, the admin web and the marketing site all read through
 * `toLegalDocumentContent`, so a malformed doc is refused the same way
 * everywhere. Plain data and pure functions only: no SDK imports.
 */

export const LEGAL_COLLECTION = 'legal_documents';

/** Subcollection of each legal doc holding one snapshot per published version. */
export const LEGAL_VERSIONS_SUBCOLLECTION = 'versions';

/**
 * Subcollection of each legal doc holding the audit log: one append-only
 * entry per change to the live doc (a publish, a restore, or the seed).
 * `firestore.rules` refuses a main-doc write that does not create the entry
 * its `lastChangeId` names in the same commit, and fills in nothing on trust:
 * the actor comes from the signed-in admin's token, the time from the server.
 * Admin-only to read, because entries carry the admin's email.
 */
export const LEGAL_CHANGELOG_SUBCOLLECTION = 'changelog';

/**
 * `publish`: a new version's snapshot was created with this change.
 * `restore`: the live doc was pointed back at a version already published.
 * `seed`: written by `scripts/seed-legal-documents.mjs`, never by a client.
 */
export type LegalChangeAction = 'publish' | 'restore' | 'seed';

/** The `changedBy` of an entry the seed script wrote; not a uid. */
export const LEGAL_SEED_ACTOR = 'seed-script';

/**
 * One audit-log entry, `legal_documents/{id}/changelog/{changeId}`.
 * `changedAt` is a Firestore Timestamp; its type differs per SDK, so it is
 * left as `unknown` here.
 */
export interface LegalChangeLogEntry {
  action: LegalChangeAction;
  /** The version live after this change. */
  version: string;
  /** The version live before it; null on the first write. */
  previousVersion: string | null;
  title: string;
  lastUpdated: string;
  /** The admin's uid, or `LEGAL_SEED_ACTOR`. */
  changedBy: string;
  /** The admin's email from their sign-in token; null for the seed. */
  changedByEmail: string | null;
  changedAt: unknown;
}

/** The only two doc ids `firestore.rules` accepts under `legal_documents`. */
export type LegalDocumentId = 'terms_of_service' | 'privacy_policy';

export const LEGAL_DOCUMENT_IDS: readonly LegalDocumentId[] = [
  'terms_of_service',
  'privacy_policy',
];

export const LEGAL_DOCUMENT_TITLES: Record<LegalDocumentId, string> = {
  terms_of_service: 'Terms of Service',
  privacy_policy: 'Privacy Policy',
};

/** Path of each document on the marketing site, appended to the legal site URL. */
export const LEGAL_DOCUMENT_PATHS: Record<LegalDocumentId, string> = {
  terms_of_service: '/terms',
  privacy_policy: '/privacy',
};

/**
 * A version id: a date, month or day, with an optional `.n` for a second
 * publish on the same date (`2025-02`, `2026-10-26`, `2026-10-26.2`).
 * `firestore.rules` checks the same pattern; change both together.
 */
export const LEGAL_VERSION_PATTERN = /^[0-9]{4}-[0-9]{2}(-[0-9]{2})?(\.[0-9]+)?$/;

/** What a reader needs from a legal doc: the main doc or a version snapshot. */
export interface LegalDocumentContent {
  title: string;
  version: string;
  /** Display text, e.g. "February 2025"; not parsed. */
  lastUpdated: string;
  /** Legal markdown; see `parseLegalMarkdown` in `./markdown`. */
  body: string;
}

function nonBlankString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Shape guard for a Firestore read (`snapshot.data()`). Returns the four
 * content fields, or null when any of them is missing, not a string, or
 * blank. Bookkeeping fields (`updatedAt`, `publishedBy`, …) are dropped.
 */
export function toLegalDocumentContent(data: unknown): LegalDocumentContent | null {
  if (typeof data !== 'object' || data === null) return null;
  const {title, version, lastUpdated, body} = data as Record<string, unknown>;
  if (
    !nonBlankString(title) ||
    !nonBlankString(version) ||
    !nonBlankString(lastUpdated) ||
    !nonBlankString(body)
  ) {
    return null;
  }
  return {title, version, lastUpdated, body};
}
